import Axios, { AxiosResponse, AxiosError } from "axios";
import { clearLocalStorageKeepingTheme } from "../context/ThemeContext";
const API = Axios.create();
API.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    // Enterprise API migration: normalize the new NestJS envelope into the shape existing call sites
    // read. New API returns { status: "success", data, page? }; the FE checks `status === "Success"`
    // and reads `.data`. Re-shape here so the ~414 call sites + components stay unchanged. Legacy
    // responses (already "Success") pass through. Paged lists carry page metadata as count/total.
    const body = response.data;
    if (body && typeof body === "object" && body.status === "success") {
      const normalized: Record<string, unknown> = { ...body, status: "Success" };
      if (body.page && typeof body.page === "object") {
        normalized.count = body.page.total;
        normalized.total = body.page.total;
        normalized.pageMeta = body.page;
      }
      // Invoice DETAIL (GET /api/v1/invoices/<ULID>) returns a single object; the v4 detail consumers
      // read `data[0]`. Wrap that one object into a 1-element array so those call sites stay unchanged.
      // Scoped tightly to the detail path (26-char ULID, no further segment) so list/lines/comments
      // (which already return arrays / their own shapes) are untouched.
      const reqUrl = response.config?.url ?? "";
      const isDetail = /\/invoices\/[0-9A-HJKMNP-TV-Za-hjkmnp-tv-z]{26}(?:$|\?)/.test(reqUrl);
      if (isDetail && normalized.data && !Array.isArray(normalized.data)) {
        normalized.data = [normalized.data];
      }
      response.data = normalized;
    }

    const stored = localStorage.getItem("API_TIME");
    const apitime = stored ? new Date(stored) : new Date();
    const endDate = new Date();
    const seconds = (endDate.getTime() - (apitime?.getTime() || new Date().getTime())) / 1000;

    if (60 * 60 < seconds) {
      // [UAT-UX item 3] keep the dark-mode preference across the session-expiry clear
      clearLocalStorageKeepingTheme();
      if (!window.location.href.includes("signin")) {
        window.location.replace("/signin");
      }
    } else {
      localStorage.setItem("API_TIME", new Date().toString());
    }
    return response;
  },
  (error: AxiosError): Promise<never> => {
    // error.response is undefined for cancelled/aborted requests (rapid page
    // navigation) and for network/throttle failures where API Gateway returns a
    // 5xx with no CORS header. Reading `.status` off undefined here threw a
    // TypeError INSIDE the interceptor, which cascaded into broken UI states
    // ("invoice does not exist", missing KVP lines). Guard it so the original
    // error propagates and each caller's catch can degrade gracefully.
    // 401 = unauthenticated (expired/invalid JWT) -> client-side sign-out. 403 is deliberately
    // NOT handled here: it is permission-denied for a real session (a user simply lacking a
    // permission must never be auto-logged-out). Surfaces that need a session-refresh affordance
    // on 403 (stale JWT after a permission-changing deploy — e.g. the upload modal, [UAT-UX
    // item 2]) handle it locally with an explicit sign-in action.
    const status = error.response?.status;
    if (status === 401) {
      // [UAT-UX item 3] keep the dark-mode preference across the auth-expiry clear
      clearLocalStorageKeepingTheme();
      if (!window.location.href.includes("signin")) {
        window.location.replace("/signin");
      }
    }
    return Promise.reject(error);
  }
);
export default API;
